// UI formatting helpers on top of the shared time formatters.

import type { RunRecord } from '../../shared/types.ts';
import type { Live } from './live.svelte.ts';

import { fmtClock, fmtDay, fmtDuration, sameDay } from '../../shared/time.ts';

export {
  fmtClock,
  fmtDay,
  fmtDelta,
  fmtDuration,
  fmtHM,
  fmtHMS,
  fmtOffsetShort,
  sameDay,
} from '../../shared/time.ts';

/** `17:14` today, `Sat 17:14` on another day — for multi-day marathons. */
export function fmtWhen(ms: number | null | undefined, now: number): string {
  if (ms == null) return '—';
  return sameDay(ms, now) ? fmtClock(ms) : `${fmtDay(ms)} ${fmtClock(ms)}`;
}

export type RunStatus = 'current' | 'done' | 'skipped' | 'upcoming' | 'setup';

export function runStatus(live: Live, index: number): RunStatus {
  const line = live.lines[index];
  if (!line) return 'upcoming';
  if (line.setupBlock) return 'setup';
  if (live.state.runs[line.key]?.skipped) return 'skipped';
  if (index === live.curIndex) return 'current';
  if (live.phase === 'complete' || (live.curIndex >= 0 && index < live.curIndex)) return 'done';
  return 'upcoming';
}

export function relTime(ms: number, now: number): string {
  const s = Math.round((now - ms) / 1000);
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86_400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86_400)}d ago`;
}

export function initials(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase();
}

/** Stable per-name hue for runner avatars. */
export function hueOf(name: string): number {
  let h = 0;
  for (const ch of name.toLowerCase()) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

/** `13m late`, `1h 05m early`, `on time` (within a minute). */
export function fmtLate(lateSec: number): string {
  if (Math.abs(lateSec) < 60) return 'on time';
  return `${fmtDuration(Math.abs(lateSec))} ${lateSec > 0 ? 'late' : 'early'}`;
}

/** `13m behind`, `4m ahead`, `on time` — the live delta's words (positive is ahead). */
export function fmtAhead(aheadSec: number): string {
  if (Math.abs(aheadSec) < 60) return 'on time';
  return `${fmtDuration(Math.abs(aheadSec))} ${aheadSec > 0 ? 'ahead' : 'behind'}`;
}

export type CheckInState = 'ready' | 'late' | 'missing' | 'none';

/** One word for a run's runners: checked in, on their way, missing, or not heard from. */
export function checkInState(rec: RunRecord | undefined): CheckInState {
  if (rec?.checkIn === 'ready') return 'ready';
  if (rec?.late) return 'late';
  return rec?.checkIn ?? 'none';
}
